import React from 'react';
import { Link } from 'react-router-dom';
import NavBar from '../components/NavBar';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <NavBar />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">
            Politique de Confidentialité
          </h1>
          <p className="text-gray-300">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">Introduction</h2>
            <p className="text-gray-300">
              Pulse ("nous", "notre" ou "nos") s'engage à protéger votre vie privée. Cette politique
              de confidentialité explique comment nous collectons, utilisons et protégeons vos
              informations personnelles lorsque vous utilisez notre application mobile.
            </p>
          </section>

          {/* Informations collectées */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">
              Informations que nous collectons
            </h2>
            <div className="space-y-4 text-gray-300">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Informations de compte
                </h3>
                <p>
                  Lorsque vous créez un compte, nous collectons : votre nom, prénom, adresse e-mail,
                  photo de profil (optionnel), et mot de passe (stocké de manière sécurisée et chiffré).
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Données de localisation
                </h3>
                <p>
                  Avec votre autorisation, nous collectons votre position géographique pour vous
                  recommander des clubs de course à proximité. Vous pouvez refuser ou révoquer cette
                  autorisation à tout moment dans les paramètres de votre appareil.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Données Strava
                </h3>
                <p>
                  Si vous choisissez de connecter votre compte Strava, nous accédons à vos activités
                  de course pour afficher vos performances et débloquer certaines fonctionnalités
                  (comme les Daily Photos).
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Photos et médias
                </h3>
                <p>
                  Vous pouvez partager des photos via la fonctionnalité Daily Photos. Ces photos sont
                  visibles par les membres de votre club uniquement s'ils ont couru ce jour-là.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Données d'utilisation
                </h3>
                <p>
                  Nous collectons des informations sur votre utilisation de l'application pour améliorer
                  nos services (pages visitées, interactions, erreurs).
                </p>
              </div>
            </div>
          </section>

          {/* Utilisation des données */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">
              Comment nous utilisons vos informations
            </h2>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li>Créer et gérer votre compte utilisateur</li>
              <li>Vous recommander des clubs de course à proximité</li>
              <li>Afficher vos activités et performances de course</li>
              <li>Permettre le partage de photos avec les membres de votre club</li>
              <li>Envoyer des notifications sur les événements et activités de votre club</li>
              <li>Améliorer et personnaliser votre expérience utilisateur</li>
              <li>Répondre à vos demandes d'assistance</li>
            </ul>
          </section>

          {/* Partage des données */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">
              Partage de vos informations
            </h2>
            <p className="text-gray-300 mb-4">
              Nous ne vendons jamais vos données personnelles. Nous ne partageons vos informations
              que dans les cas suivants :
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li>
                <strong className="text-white">Avec les membres de votre club :</strong> Votre nom,
                photo de profil, et activités partagées sont visibles par les membres des clubs dont
                vous faites partie
              </li>
              <li>
                <strong className="text-white">Strava :</strong> Si vous connectez votre compte Strava,
                nous accédons à vos données d'activité conformément à leur politique de confidentialité
              </li>
              <li>
                <strong className="text-white">Services tiers :</strong> Nous utilisons des services
                d'hébergement et d'analyse pour faire fonctionner l'application
              </li>
              <li>
                <strong className="text-white">Obligations légales :</strong> Si requis par la loi ou
                pour protéger nos droits
              </li>
            </ul>
          </section>

          {/* Sécurité */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">
              Sécurité de vos données
            </h2>
            <p className="text-gray-300">
              Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées
              pour protéger vos informations personnelles contre tout accès non autorisé, modification,
              divulgation ou destruction. Vos mots de passe sont chiffrés et nous utilisons des
              connexions sécurisées (HTTPS) pour toutes les transmissions de données.
            </p>
          </section>

          {/* Vos droits */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">
              Vos droits
            </h2>
            <p className="text-gray-300 mb-4">Vous avez le droit de :</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li>Accéder à vos données personnelles</li>
              <li>Corriger vos informations inexactes ou incomplètes</li>
              <li>Supprimer votre compte et vos données</li>
              <li>Révoquer votre consentement à tout moment</li>
              <li>Exporter vos données</li>
              <li>Vous opposer au traitement de vos données</li>
            </ul>
            <p className="text-gray-300 mt-4">
              Pour exercer ces droits, contactez-nous à :{' '}
              <a href="mailto:29beaugarcon@gmail.com" className="text-blue-400 hover:text-blue-300">
                29beaugarcon@gmail.com
              </a>
            </p>
          </section>

          {/* Conservation des données */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">
              Conservation des données
            </h2>
            <p className="text-gray-300">
              Nous conservons vos données personnelles aussi longtemps que votre compte est actif ou
              que cela est nécessaire pour vous fournir nos services. Si vous supprimez votre compte,
              vos données seront supprimées dans un délai de 30 jours, sauf si nous sommes tenus de
              les conserver pour des raisons légales.
            </p>
          </section>

          {/* Cookies et technologies similaires */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">
              Cookies et technologies similaires
            </h2>
            <p className="text-gray-300">
              Nous utilisons des cookies et des technologies similaires pour améliorer votre expérience,
              analyser l'utilisation de l'application et mémoriser vos préférences. Vous pouvez
              contrôler l'utilisation des cookies via les paramètres de votre navigateur.
            </p>
          </section>

          {/* Modifications */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">
              Modifications de cette politique
            </h2>
            <p className="text-gray-300">
              Nous pouvons mettre à jour cette politique de confidentialité de temps en temps. Nous
              vous informerons de tout changement important en publiant la nouvelle politique sur cette
              page et en mettant à jour la date de "dernière mise à jour" en haut de cette page.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">
              Nous contacter
            </h2>
            <p className="text-gray-300">
              Si vous avez des questions concernant cette politique de confidentialité, veuillez nous
              contacter à :
            </p>
            <div className="mt-4 p-4 bg-white/5 rounded-lg">
              <p className="text-white font-semibold">Pulse</p>
              <p className="text-gray-300">
                Email :{' '}
                <a href="mailto:29beaugarcon@gmail.com" className="text-blue-400 hover:text-blue-300">
                  29beaugarcon@gmail.com
                </a>
              </p>
            </div>
          </section>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-12">
          <Link
            to="/"
            className="text-blue-400 hover:text-blue-300 font-semibold"
          >
            ← Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
}
